import React, { useState, useEffect, useContext } from 'react'
import { useHistory } from 'react-router-dom'
import axios from 'axios'
import DateFnsUtils from '@date-io/date-fns'
import {
  Grid,
  IconButton,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Button,
  CircularProgress,
  Divider,
  Typography
} from '@material-ui/core'
import { Alert, AlertTitle } from '@material-ui/lab'
import { Close, GetApp } from '@material-ui/icons'
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers'
import GridHeader from 'components/common/GridHeader'
import { ReducerContext } from 'App'
import { isEthanolTerminal } from 'constants/productTypes'

const Reports = () => {
  const {
    state: {
      user: { permissions },
      currentTerminal,
      config
    }
  } = useContext(ReducerContext)
  const history = useHistory()

  useEffect(() => {
    !permissions.includes('user') && history.push('/unauthorized')
  }, [permissions, history])

  // Show ethanol/cement reports only for multi-product terminals (e.g., Binghamton)
  const isMultiProduct = isEthanolTerminal(currentTerminal)
  const hasCement = isMultiProduct
  const hasEthanol = isMultiProduct

  const [start_date, setStart_date] = useState(null)
  const [end_date, setEnd_date] = useState(null)
  const [open, setOpen] = useState(false)
  const [error, setError] = useState(null)
  const [bolsLoading, setBolsLoading] = useState(false)
  const [bolsWInvInterLoading, setBolsWInvInterLoading] = useState(false)
  const [cementBolsLoading, setCementBolsLoading] = useState(false)
  const [cementBolsWInvLoading, setCementBolsWInvLoading] = useState(false)
  const [ethanolBolsLoading, setEthanolBolsLoading] = useState(false)
  const [ethanolBolsWInvLoading, setEthanolBolsWInvLoading] = useState(false)
  const [railcarsEmptiedLoading, setRailcarsEmptiedLoading] = useState(false)
  const [railcarsReleasedLoading, setRailcarsReleasedLoading] = useState(false)
  const [railcarInventoryLoading, setRailcarInventoryLoading] = useState(false)

  const handleDateChange = (date, type) => {
    setOpen(false)
    setError(null)
    if (date instanceof Date) {
      type === 'start'
        ? setStart_date(new Date(date.setHours(0, 0, 0, 0)))
        : setEnd_date(new Date(date.setHours(23, 59, 59, 59)))
    }
  }

  const formatDate = date => {
    if (!date) return ''
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  // The export routes answer errors as JSON, but these requests ask for text, so axios leaves the
  // body a string. Without reading it the user sees "Bad Request" instead of which input was wrong.
  const serverMessage = err => {
    const data = err.response?.data
    if (!data) return null
    if (typeof data === 'object') return data.error ?? null
    try {
      return JSON.parse(data).error ?? null
    } catch {
      return null
    }
  }

  const downloadCSV = async (endpoint, filename, setLoading, productCategory = null, requireDates = true) => {
    if (requireDates && (!start_date || !end_date)) {
      setOpen(true)
      return
    }

    try {
      setLoading(true)
      setError(null)

      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
      const params = new URLSearchParams({
        ...(requireDates && { start_date: formatDate(start_date), end_date: formatDate(end_date) }),
        terminal: currentTerminal,
        // requirePermissionAndTerminal reads currentTerminal; the older BOL exports still read
        // `terminal`, so both go on every request rather than renaming one out from under them.
        currentTerminal,
        timezone: userTimezone,
        ...(productCategory && { product_category: productCategory })
      })

      // axios rather than fetch so the app's Bearer interceptor attaches the auth token.
      const response = await axios.get(`${config.LOGITRAC_API_URL}api/reports/${endpoint}?${params}`, {
        responseType: 'text',
        headers: { Accept: 'text/csv' }
      })

      const text = response.data
      const lines = String(text).trim().split('\n')

      if (lines.length <= 1) {
        setOpen(true)
        setLoading(false)
        return
      }

      const csvBlob = new Blob([text], { type: 'text/csv' })
      const url = window.URL.createObjectURL(csvBlob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename.replace(/ /g, '_')
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Error downloading CSV:', err)
      setError(err.response ? `Failed to generate report: ${serverMessage(err) ?? err.response.statusText}` : err.message)
    } finally {
      setLoading(false)
    }
  }

  const ReportButton = ({ loading, onClick, disabled, children }) => (
    <Button
      variant="contained"
      color="primary"
      startIcon={loading ? <CircularProgress size={20} /> : <GetApp />}
      disabled={disabled || loading}
      onClick={onClick}
      style={{ margin: '10px' }}
    >
      {loading ? 'Generating...' : children}
    </Button>
  )

  const ReportSection = ({ title, subtitle, children }) => (
    <>
      <Divider style={{ margin: '16px 10px' }} />
      <Typography variant="subtitle2" style={{ marginBottom: subtitle ? '2px' : '8px', marginLeft: '10px' }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: '8px', marginLeft: '10px' }}>
          {subtitle}
        </Typography>
      )}
      {children}
    </>
  )

  return (
    <Grid container className="GridContainer">
      <GridHeader
        h4="Export Reports"
        p="Date Range picker and export functionality for generating CSV files that can be handled &amp; delivered offline."
      >
        <Grid container className="GridContainer">
          <Grid item xs={12} sm={12} md={12} className="GridItem">
            <Grid container className="GridContainer">
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <Grid item xs={6} md={6} className="GridItem">
                  <KeyboardDatePicker
                    margin="normal"
                    id="start-date-picker-dialog"
                    required
                    label="Start Date"
                    format="MM/dd/yyyy"
                    value={start_date}
                    onChange={e => handleDateChange(e, 'start')}
                    KeyboardButtonProps={{ 'aria-label': 'change date' }}
                    autoOk={true}
                    disableFuture
                    fullWidth
                    className="getBold"
                  />
                </Grid>
                <Grid item xs={6} md={6} className="GridItem">
                  <KeyboardDatePicker
                    margin="normal"
                    id="end-date-picker-dialog"
                    label="End Date"
                    format="MM/dd/yyyy"
                    value={end_date}
                    onChange={e => handleDateChange(e, 'end')}
                    KeyboardButtonProps={{ 'aria-label': 'change date' }}
                    autoOk
                    disableFuture
                    fullWidth
                    required
                    className="getBold"
                  />
                </Grid>
              </MuiPickersUtilsProvider>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={12} md={12} className="GridItem" style={{ marginTop: '20px' }}>
            <Grid container direction="column" justifyContent="center" alignItems="center">
              <Card variant="outlined">
                <CardHeader subheader="Export CSV Reports" />
                <CardContent>
                  <Typography variant="subtitle2" style={{ marginBottom: '8px', marginLeft: '10px' }}>
                    Sand Reports
                  </Typography>
                  <ReportButton
                    loading={bolsLoading}
                    disabled={!start_date || !end_date}
                    onClick={() =>
                      downloadCSV(
                        'export/bols',
                        `Sand_BOLs_${formatDate(start_date)}-${formatDate(end_date)}.csv`,
                        setBolsLoading,
                        'sand'
                      )
                    }
                  >
                    BOLs
                  </ReportButton>

                  <ReportButton
                    loading={bolsWInvInterLoading}
                    disabled={!start_date || !end_date}
                    onClick={() =>
                      downloadCSV(
                        'export/bols-inventory',
                        `Sand_BOLs_Inventory_${formatDate(start_date)}-${formatDate(end_date)}.csv`,
                        setBolsWInvInterLoading,
                        'sand'
                      )
                    }
                  >
                    BOLs w/ Inventory
                  </ReportButton>

                  {hasCement && (
                    <ReportSection title="Cement Reports">
                      <ReportButton
                        loading={cementBolsLoading}
                        disabled={!start_date || !end_date}
                        onClick={() =>
                          downloadCSV(
                            'export/bols',
                            `Cement_BOLs_${formatDate(start_date)}-${formatDate(end_date)}.csv`,
                            setCementBolsLoading,
                            'cement'
                          )
                        }
                      >
                        BOLs
                      </ReportButton>

                      <ReportButton
                        loading={cementBolsWInvLoading}
                        disabled={!start_date || !end_date}
                        onClick={() =>
                          downloadCSV(
                            'export/bols-inventory',
                            `Cement_BOLs_Inventory_${formatDate(start_date)}-${formatDate(end_date)}.csv`,
                            setCementBolsWInvLoading,
                            'cement'
                          )
                        }
                      >
                        BOLs w/ Inventory
                      </ReportButton>
                    </ReportSection>
                  )}

                  {hasEthanol && (
                    <ReportSection title="Ethanol Reports">
                      <ReportButton
                        loading={ethanolBolsLoading}
                        disabled={!start_date || !end_date}
                        onClick={() =>
                          downloadCSV(
                            'export/bols',
                            `Ethanol_BOLs_${formatDate(start_date)}-${formatDate(end_date)}.csv`,
                            setEthanolBolsLoading,
                            'ethanol'
                          )
                        }
                      >
                        BOLs
                      </ReportButton>

                      <ReportButton
                        loading={ethanolBolsWInvLoading}
                        disabled={!start_date || !end_date}
                        onClick={() =>
                          downloadCSV(
                            'export/bols-inventory',
                            `Ethanol_BOLs_Inventory_${formatDate(start_date)}-${formatDate(end_date)}.csv`,
                            setEthanolBolsWInvLoading,
                            'ethanol'
                          )
                        }
                      >
                        BOLs w/ Tank Cars
                      </ReportButton>
                    </ReportSection>
                  )}

                  <ReportSection
                    title="Railcar Reports"
                    subtitle="Billable Releases is the invoice source. Railcars Emptied shows cars that emptied in the range, including ones still on the ground, so a car that emptied but was never released is visible. Both cover the date range above; Railcar Inventory is a live snapshot of what is on the ground right now and ignores the dates."
                  >
                    <ReportButton
                      loading={railcarsReleasedLoading}
                      disabled={!start_date || !end_date}
                      onClick={() =>
                        downloadCSV(
                          'export/railcars-released',
                          `Billable_Releases_${formatDate(start_date)}-${formatDate(end_date)}.csv`,
                          setRailcarsReleasedLoading
                        )
                      }
                    >
                      Billable Releases
                    </ReportButton>

                    <ReportButton
                      loading={railcarsEmptiedLoading}
                      disabled={!start_date || !end_date}
                      onClick={() =>
                        downloadCSV(
                          'export/railcars-emptied',
                          `Railcars_Emptied_${formatDate(start_date)}-${formatDate(end_date)}.csv`,
                          setRailcarsEmptiedLoading
                        )
                      }
                    >
                      Railcars Emptied
                    </ReportButton>

                    <ReportButton
                      loading={railcarInventoryLoading}
                      onClick={() =>
                        downloadCSV(
                          'export/railcar-inventory',
                          'Railcar_Inventory.csv',
                          setRailcarInventoryLoading,
                          null,
                          false
                        )
                      }
                    >
                      Railcar Inventory (live)
                    </ReportButton>
                  </ReportSection>

                  <Collapse in={open}>
                    <Alert
                      severity="warning"
                      action={
                        <IconButton onClick={() => setOpen(false)}>
                          <Close fontSize="inherit" />
                        </IconButton>
                      }
                    >
                      <AlertTitle>No Data</AlertTitle>
                      No data to export for selected dates. — <strong>Please select different dates.</strong>
                    </Alert>
                  </Collapse>

                  {error && (
                    <Alert
                      severity="error"
                      action={
                        <IconButton onClick={() => setError(null)}>
                          <Close fontSize="inherit" />
                        </IconButton>
                      }
                    >
                      <AlertTitle>Error</AlertTitle>
                      {error}
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </GridHeader>
    </Grid>
  )
}

export default Reports
